import { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import Orb from './components/Orb/Orb.jsx';
import VoiceInput from './components/Input/VoiceInput.jsx';
import DaemonTag from './components/DaemonTag/DaemonTag.jsx';
import { useTTS, unlockAudioContext, isAudioUnlocked } from './hooks/useTTS.js';

/* ═══════════════════════════════════════════════════════════════
   LANDING — Voice-first greeter using the crystal orb
   The daemon greets the visitor, introduces the product,
   and routes them (seeker / recruiter / demo) through conversation.
   ═══════════════════════════════════════════════════════════════ */

// Discriminated union: { ok: true, ...payload } | { ok: false, error: 'network'|'http_4xx'|'http_5xx'|'parse' }
// Never returns a fabricated response string — callers MUST branch on `ok` and never write
// failure cases to conversation history. See the invariant comment in VoidInterface.handleSubmit.
async function callGreet(messages) {
  let r;
  try {
    r = await fetch('/api/greet', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages }),
    });
  } catch {
    return { ok: false, error: 'network' };
  }
  if (!r.ok) {
    return { ok: false, error: r.status >= 500 ? 'http_5xx' : 'http_4xx' };
  }
  try {
    const data = await r.json();
    return { ok: true, ...data };
  } catch {
    return { ok: false, error: 'parse' };
  }
}

function apiErrorMessage(code) {
  switch (code) {
    case 'network':  return 'Connection lost — try again';
    case 'http_4xx': return 'Request rejected — try again';
    case 'http_5xx': return 'Server hiccup — try again';
    case 'parse':    return 'Bad response — try again';
    default:         return 'Something went wrong — try again';
  }
}

function LandingPage({ onSeeker, onRecruiter, onZach }) {
  const [uiPhase,     setUiPhase]     = useState('init');
  const [textPhase,   setTextPhase]   = useState('hidden');
  const [displayText, setDisplayText] = useState('');
  const [apiError,    setApiError]    = useState(null);
  const [started,     setStarted]     = useState(() => isAudioUnlocked());  // skip gate if audio already unlocked

  const history     = useRef([]);
  const timers      = useRef([]);
  const speakCancel = useRef(false);

  const tts = useTTS();

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  const addTimer    = (fn, ms) => { const id = setTimeout(fn, ms); timers.current.push(id); return id; };

  // ── Surface text in the orb ────────────────────────────────────────────────
  const surfaceText = useCallback((text) => {
    clearTimers();
    setDisplayText(text);
    setTextPhase('appearing');
    addTimer(() => setTextPhase('showing'), 1400);
    const words  = text.trim().split(/\s+/).length;
    const readMs = Math.min(16000, Math.max(4000, words * 260));
    addTimer(() => {
      setTextPhase('fading');
      addTimer(() => { setTextPhase('hidden'); setDisplayText(''); }, 950);
    }, 1400 + readMs);
  }, []); // eslint-disable-line

  // ── Play TTS then activate mic ─────────────────────────────────────────────
  const speakAndListen = useCallback(async (text) => {
    speakCancel.current = false;
    try {
      await tts.play(text);
    } catch {
      const words  = text.trim().split(/\s+/).length;
      await new Promise(r => {
        const id = setTimeout(r, Math.min(12000, Math.max(2500, words * 180)));
        timers.current.push(id);
      });
    }
    if (speakCancel.current) return;
    setUiPhase('listening');
  }, [tts]); // eslint-disable-line

  // ── Awaken: user's first tap unlocks audio and fires the greeting ─────────
  const awaken = useCallback(() => {
    if (started) return;
    unlockAudioContext();
    setStarted(true);
  }, [started]);

  // ── Init: daemon's greeting (only fires after user taps to summon) ────────
  useEffect(() => {
    if (!started) return;
    const msgs = [{ role: 'user', content: 'Hello, I just arrived.' }];
    callGreet(msgs).then((result) => {
      if (!result.ok) {
        setApiError(apiErrorMessage(result.error));
        setUiPhase('listening');
        return;
      }
      const { response, route } = result;
      history.current = [...msgs, { role: 'assistant', content: response }];

      // If routed on first message (unlikely but handle it)
      if (route) {
        handleRoute(route);
        return;
      }

      setUiPhase('speaking');
      surfaceText(response);
      speakAndListen(response);
    });

    return () => { clearTimers(); tts.stop(); };
  }, [started]); // eslint-disable-line

  // ── Route to the right experience ──────────────────────────────────────────
  const handleRoute = useCallback((route) => {
    unlockAudioContext();
    if (route === 'recruiter') {
      onRecruiter();
    } else if (route === 'demo') {
      onZach();
    } else {
      onSeeker();
    }
  }, [onSeeker, onRecruiter, onZach]);

  // ── User responds (voice or keyboard) ──────────────────────────────────────
  const handleSubmit = useCallback(async (text) => {
    console.log('[Landing] handleSubmit:', text, '| uiPhase:', uiPhase);
    if (uiPhase === 'thinking' || uiPhase === 'init') {
      console.log('[Landing] handleSubmit BLOCKED — uiPhase is', uiPhase);
      return;
    }

    speakCancel.current = true;
    tts.stop();
    clearTimers();
    setTextPhase('hidden');
    setDisplayText('');
    setApiError(null);
    setUiPhase('thinking');

    unlockAudioContext();

    // Build proposed history without committing — only commit on success.
    const proposedHistory = [...history.current, { role: 'user', content: text }];

    const result = await callGreet(proposedHistory);
    if (!result.ok) {
      setApiError(apiErrorMessage(result.error));
      setUiPhase('listening');
      return;
    }
    const { response, route } = result;
    console.log('[Landing] callGreet returned, route:', route, '| response:', response.slice(0, 60));
    history.current = [...proposedHistory, { role: 'assistant', content: response }];

    if (route) {
      // Show the daemon's final routing message briefly, then transition
      setUiPhase('speaking');
      surfaceText(response);
      speakCancel.current = false;
      try {
        await tts.play(response);
      } catch {
        await new Promise(r => setTimeout(r, 2000));
      }
      handleRoute(route);
      return;
    }

    setUiPhase('speaking');
    surfaceText(response);
    speakAndListen(response);
  }, [uiPhase, tts, handleRoute, surfaceText, speakAndListen]); // eslint-disable-line

  return (
    <div className="void-screen" onClick={awaken}>
      <div aria-live="polite" className="sr-only">{displayText}</div>
      <div className="void-ambient" aria-hidden="true" />

      <Orb
        text=""
        textPhase="hidden"
        uiPhase={uiPhase}
        exchangeCount={0}
        material={null}
        materialBlend={0}
        onShatterComplete={() => {}}
      />

      <div className="orb-input-anchor">
        <VoiceInput
          onSubmit={handleSubmit}
          autoListen={uiPhase === 'listening'}
          disabled={uiPhase === 'init' || uiPhase === 'thinking' || uiPhase === 'speaking'}
        />
      </div>

      {!started && (
        <div className="awaken-hint">Touch the orb to summon</div>
      )}

      {apiError && (
        <div
          role="alert"
          aria-live="assertive"
          style={{
            position: 'absolute', bottom: '90px', left: '50%', transform: 'translateX(-50%)',
            color: '#e8c97a', fontSize: '13px', opacity: 0.9, pointerEvents: 'none',
          }}
        >
          {apiError}
        </div>
      )}

      {/* Fallback paths — appear once user has summoned, in case voice fails */}
      {started && uiPhase !== 'init' && (
        <div className="route-fallback" onClick={(e) => e.stopPropagation()}>
          <button
            className="route-link"
            onClick={() => { unlockAudioContext(); tts.stop(); onSeeker(); }}
          >
            Candidate
          </button>
          <span className="route-sep">·</span>
          <button
            className="route-link"
            onClick={() => { unlockAudioContext(); tts.stop(); onRecruiter(); }}
          >
            Recruiter
          </button>
          <span className="route-sep">·</span>
          <button
            className="route-link"
            onClick={() => { unlockAudioContext(); tts.stop(); onZach(); }}
          >
            Meet Zach's Daemon
          </button>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   API
   ═══════════════════════════════════════════ */

// Discriminated union: { ok: true, response, ready, profile?, showUpload? } | { ok: false, error }
// Never returns a fabricated response string — callers MUST branch on `ok`.
async function callBuild(messages, exchangeCount, resumeText) {
  let r;
  try {
    r = await fetch('/api/build', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages, exchangeCount, resumeText: resumeText || null }),
    });
  } catch {
    return { ok: false, error: 'network' };
  }
  if (!r.ok) {
    return { ok: false, error: r.status >= 500 ? 'http_5xx' : 'http_4xx' };
  }
  try {
    const data = await r.json();
    return { ok: true, ...data };
  } catch {
    return { ok: false, error: 'parse' };
  }
}

/* ═══════════════════════════════════════════════════════════════
   VOID INTERFACE — the ritual space

   uiPhase:
     'init'       — loading first daemon message
     'speaking'   — TTS audio playing (orb intensifies)
     'listening'  — mic active, waiting for user (orb absorbs)
     'thinking'   — API call in progress (orb breathes deep)
     'idle'       — keyboard fallback (no TTS, no mic active)
     'shattering' — orb shatter sequence

   textPhase: 'hidden' | 'appearing' | 'showing' | 'fading'
   ═══════════════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════
   RESUME UPLOAD
   ═══════════════════════════════════════════ */

function ResumeUpload({ onUploaded, disabled }) {
  const [state, setState] = useState('idle'); // idle | uploading | error
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const processFile = useCallback(async (file) => {
    if (!file) return;
    const ok = file.type === 'application/pdf'
      || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      || file.name.toLowerCase().endsWith('.pdf')
      || file.name.toLowerCase().endsWith('.docx');
    if (!ok) { setState('error'); return; }

    setState('uploading');
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const r = await fetch('/api/resume', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: base64, mimetype: file.type, filename: file.name }),
      });
      if (!r.ok) throw new Error('extraction failed');
      const { text } = await r.json();
      onUploaded(text);
    } catch {
      setState('error');
    }
  }, [onUploaded]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled || state === 'uploading') return;
    processFile(e.dataTransfer.files[0]);
  }, [disabled, state, processFile]);

  const onFileChange = useCallback((e) => {
    processFile(e.target.files[0]);
    e.target.value = '';
  }, [processFile]);

  return (
    <div
      className={`resume-upload${dragOver ? ' ru-drag' : ''}${state === 'uploading' ? ' ru-uploading' : ''}${state === 'error' ? ' ru-error' : ''}`}
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      onClick={() => !disabled && state !== 'uploading' && inputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && inputRef.current?.click()}
      aria-label="Upload resume"
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        onChange={onFileChange}
        style={{ display: 'none' }}
        disabled={disabled || state === 'uploading'}
      />
      <span className="ru-label">
        {state === 'uploading' && 'Reading…'}
        {state === 'error'    && 'Try again — PDF or DOCX only'}
        {state === 'idle'     && 'Drop your resume here'}
      </span>
    </div>
  );
}

function VoidInterface({ onDeploy }) {
  const [uiPhase,    setUiPhase]    = useState('init');
  const [textPhase,  setTextPhase]  = useState('hidden');
  const [displayText,setDisplayText]= useState('');
  const [exchangeCount, setExchangeCount] = useState(0);
  const [resumeText,  setResumeText]  = useState(null);
  const [showUpload,  setShowUpload]  = useState(false);
  const [apiError,    setApiError]    = useState(null);
  const [started,    setStarted]    = useState(() => isAudioUnlocked());  // skip gate if audio already unlocked

  const [material,      setMaterial]      = useState(null);
  const [materialBlend, setMaterialBlend] = useState(0);
  const [daemonAnimal,  setDaemonAnimal]  = useState(null);
  const [tagVisibility, setTagVisibility] = useState('hidden');

  const history        = useRef([]);
  const pendingProfile = useRef(null);
  const deployResult   = useRef(null);
  const shatterDone    = useRef(false);
  const timers         = useRef([]);
  const speakCancel    = useRef(false);  // set true to abort speakAndListen

  const tts = useTTS();

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  const addTimer    = (fn, ms) => { const id = setTimeout(fn, ms); timers.current.push(id); return id; };

  // ── Surface text (visual only — no phase transitions) ─────────────────────
  const surfaceText = useCallback((text) => {
    clearTimers();
    setDisplayText(text);
    setTextPhase('appearing');
    addTimer(() => setTextPhase('showing'), 1400);

    const words  = text.trim().split(/\s+/).length;
    const readMs = Math.min(16000, Math.max(4000, words * 240));

    addTimer(() => {
      setTextPhase('fading');
      addTimer(() => { setTextPhase('hidden'); setDisplayText(''); }, 950);
    }, 1400 + readMs);
  }, []); // eslint-disable-line

  // ── Play TTS then activate mic (or shatter if deploy is ready) ─────────────
  const speakAndListen = useCallback(async (text) => {
    speakCancel.current = false;
    try {
      await tts.play(text);
    } catch {
      // TTS failed (no key, autoplay blocked, network) — brief pause then listen
      await new Promise(r => {
        const words  = text.trim().split(/\s+/).length;
        const waitMs = Math.min(12000, Math.max(2500, words * 180));
        const id = setTimeout(r, waitMs);
        timers.current.push(id);
      });
    }
    if (speakCancel.current) return;    // cancelled by handleSubmit
    if (pendingProfile.current) {
      setUiPhase('shattering');
    } else {
      setUiPhase('listening');
    }
  }, [tts]); // eslint-disable-line

  // ── Deploy API call (fires in background, parallel to close message) ───────
  const triggerDeployCall = useCallback(async (profile) => {
    try {
      const r = await fetch('/api/deploy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversation: history.current, profile }),
      });
      if (!r.ok) throw new Error('deploy_failed');
      const d = await r.json();
      deployResult.current = { id: d.id, data: d };
      if (shatterDone.current) onDeploy(d.id, d);
    } catch (err) {
      console.error('Deploy failed:', err);
    }
  }, [onDeploy]);

  // ── Awaken: user's first tap unlocks audio and fires the greeting ─────────
  const awaken = useCallback(() => {
    if (started) return;
    unlockAudioContext();
    setStarted(true);
  }, [started]);

  // ── Init: daemon's first message (only fires after user taps to summon) ────
  useEffect(() => {
    if (!started) return;
    const msgs = [{ role: 'user', content: 'Hello.' }];
    callBuild(msgs, 1, null).then((result) => {
      if (!result.ok) {
        setApiError(apiErrorMessage(result.error));
        setUiPhase('listening');
        return;
      }
      const { response, ready, profile, showUpload: su } = result;
      history.current = [...msgs, { role: 'assistant', content: response }];
      setExchangeCount(1);

      if (ready && profile) {
        pendingProfile.current = profile;
        triggerDeployCall(profile);
        if (profile.material) setMaterial(profile.material);
        if (profile.animal)   setDaemonAnimal(profile.animal);
      }
      if (su) setShowUpload(true);

      setUiPhase('speaking');
      surfaceText(response);
      speakAndListen(response);
    });

    return () => { clearTimers(); tts.stop(); };
  }, [started]); // eslint-disable-line

  // ── Resume uploaded — synthesise a user turn and continue ─────────────────
  const handleResumeUpload = useCallback(async (text) => {
    setResumeText(text);
    setShowUpload(false);
    if (uiPhase === 'thinking' || uiPhase === 'shattering') return;

    speakCancel.current = true;
    tts.stop();
    clearTimers();
    setTextPhase('hidden');
    setDisplayText('');
    setApiError(null);
    setUiPhase('thinking');

    const synthetic = 'I\'ve shared my resume with you.';
    const proposedExchange = exchangeCount + 1;
    const proposedHistory = [...history.current, { role: 'user', content: synthetic }];

    const result = await callBuild(proposedHistory, proposedExchange, text);
    if (!result.ok) {
      // Resume text is preserved in state — user can speak to retry.
      setApiError(apiErrorMessage(result.error));
      setUiPhase('listening');
      return;
    }
    const { response, ready, profile, showUpload: su } = result;
    history.current = [...proposedHistory, { role: 'assistant', content: response }];
    setExchangeCount(proposedExchange);

    if (ready && profile) {
      pendingProfile.current = profile;
      triggerDeployCall(profile);
      if (profile.material && !material) setMaterial(profile.material);
      if (profile.animal && !daemonAnimal) setDaemonAnimal(profile.animal);
    }
    if (su) setShowUpload(true);

    setUiPhase('speaking');
    surfaceText(response);
    speakAndListen(response);
  }, [uiPhase, exchangeCount, material, daemonAnimal, tts, triggerDeployCall, surfaceText, speakAndListen]); // eslint-disable-line

  // ── User submits (voice or keyboard) ──────────────────────────────────────
  // Invariant: history.current only contains turns that genuinely occurred.
  // API errors must never produce a fake assistant turn — they surface as UI errors
  // and the user retries without conversation state being mutated.
  const handleSubmit = useCallback(async (text) => {
    if (uiPhase === 'thinking' || uiPhase === 'shattering' || uiPhase === 'init') return;

    speakCancel.current = true;
    tts.stop();
    clearTimers();

    setTextPhase('hidden');
    setDisplayText('');
    setApiError(null);
    setUiPhase('thinking');
    setShowUpload(false);

    // Build proposed history without committing — only commit on success.
    const proposedExchange = exchangeCount + 1;
    const proposedHistory = [...history.current, { role: 'user', content: text }];

    const result = await callBuild(proposedHistory, proposedExchange, resumeText);
    if (!result.ok) {
      // Per invariant: do not mutate history.current or exchangeCount on failure.
      setApiError(apiErrorMessage(result.error));
      setUiPhase('listening');
      return;
    }
    const { response, ready, profile, showUpload: su } = result;
    history.current = [...proposedHistory, { role: 'assistant', content: response }];
    setExchangeCount(proposedExchange);

    if (ready && profile) {
      pendingProfile.current = profile;
      triggerDeployCall(profile);
      if (profile.material && !material) setMaterial(profile.material);
      if (profile.animal && !daemonAnimal) setDaemonAnimal(profile.animal);
    }
    if (su) setShowUpload(true);

    setUiPhase('speaking');
    surfaceText(response);
    speakAndListen(response);
  }, [uiPhase, exchangeCount, material, daemonAnimal, resumeText, tts, triggerDeployCall, surfaceText, speakAndListen]);

  // ── Shatter complete ───────────────────────────────────────────────────────
  const handleShatterComplete = useCallback(() => {
    shatterDone.current = true;
    if (deployResult.current) {
      onDeploy(deployResult.current.id, deployResult.current.data);
    }
  }, [onDeploy]);

  return (
    <div className="void-screen" onClick={awaken}>
      {/* Screen-reader mirror */}
      <div aria-live="polite" className="sr-only">{displayText}</div>

      {/* Ambient glow breathes with orb */}
      <div className="void-ambient" aria-hidden="true" />

      {/* Awaken hint — disappears once user taps */}
      {!started && (
        <div className="awaken-hint">Touch the orb to summon</div>
      )}

      {/* The orb */}
      <Orb
        text=""
        textPhase="hidden"
        uiPhase={uiPhase}
        exchangeCount={exchangeCount}
        material={material}
        materialBlend={materialBlend}
        onShatterComplete={handleShatterComplete}
      />

      {/* Voice input — hidden on desktop, mic button on mobile */}
      <div className="orb-input-anchor">
        <VoiceInput
          onSubmit={handleSubmit}
          autoListen={uiPhase === 'listening'}
          disabled={uiPhase === 'init' || uiPhase === 'thinking' || uiPhase === 'speaking' || uiPhase === 'shattering'}
        />
      </div>

      {/* Resume upload zone — appears when daemon asks for it */}
      {showUpload && (
        <ResumeUpload
          onUploaded={handleResumeUpload}
          disabled={uiPhase === 'thinking' || uiPhase === 'shattering'}
        />
      )}

      {apiError && (
        <div
          role="alert"
          aria-live="assertive"
          style={{
            position: 'absolute', bottom: '90px', left: '50%', transform: 'translateX(-50%)',
            color: '#e8c97a', fontSize: '13px', opacity: 0.9, pointerEvents: 'none',
          }}
        >
          {apiError}
        </div>
      )}

      {/* Daemon tag — ghost during build, full at reveal */}
      {(daemonAnimal || material) && (
        <div
          className={`void-daemon-tag ${tagVisibility === 'born' ? 'tag-born' : tagVisibility === 'ghost' ? 'tag-ghost' : ''}`}
          style={{ color: '#9060e0' }}
        >
          <DaemonTag
            material={material}
            animal={daemonAnimal}
            visibility={tagVisibility}
          />
        </div>
      )}

    </div>
  );
}

/* ═══════════════════════════════════════════
   DEPLOY SUCCESS
   ═══════════════════════════════════════════ */

function DeploySuccess({ daemonId, deployedProfile, onBack }) {
  const url     = `${window.location.origin}?talk=${daemonId}`;
  const [copied, setCopied] = useState(false);
  const [show,   setShow]   = useState(false);

  const tag      = deployedProfile?.tag      || `${deployedProfile?.material || ''} ${deployedProfile?.animal || 'Daemon'}`.trim();
  const material = deployedProfile?.material || null;
  const animal   = deployedProfile?.animal   || tag;
  const essence  = deployedProfile?.essence  || '';

  useEffect(() => {
    const t = setTimeout(() => setShow(true), 400);
    return () => clearTimeout(t);
  }, []);

  const copy = () => {
    navigator.clipboard?.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="screen void-screen" style={{ justifyContent: 'center', paddingBottom: 0 }}>
      <div className="void-ambient" aria-hidden="true" />
      <div className={`deploy-success${show ? ' deploy-success-visible' : ''}`}>
        <div className="ds-tag" style={{ color: '#9060e0' }}>
          <DaemonTag material={material} animal={animal} visibility={show ? 'display' : 'hidden'} />
        </div>
        <p className="ds-free" aria-live="polite">Your daemon is free.</p>
        <p className="ds-essence">{essence}</p>
        <div className="ds-link">
          <code className="ds-url">{url}</code>
          <button className="btn-ghost sm" onClick={copy}>
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
        </div>
        <p className="lc-hint">Share this link. Anyone who opens it can talk to your daemon.</p>
        <button className="btn-link mt1" onClick={onBack}>← Return home</button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   SHARED COMPONENTS (recruiter / zach side)
   ═══════════════════════════════════════════ */

function Badge({ animal, size }) {
  const s       = size || 'md';
  const initial = animal ? animal.charAt(0).toUpperCase() : '△';
  return (
    <div className={`badge badge-${s}`}>
      <div className="badge-jade">
        <div className="badge-gold">
          <div className="badge-orb-badge">
            <span className="badge-char">{initial}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Message({ role, content, avatar }) {
  return (
    <div className={`msg msg-${role}`}>
      {role === 'assistant' && <div className="msg-av">{avatar || '△'}</div>}
      <div className="msg-bub"><div className="msg-txt">{content}</div></div>
    </div>
  );
}

function Typing({ avatar }) {
  return (
    <div className="msg msg-assistant">
      <div className="msg-av">{avatar || '△'}</div>
      <div className="msg-bub typing"><div className="dots"><span /><span /><span /></div></div>
    </div>
  );
}

function useViewportHeight(ref) {
  useEffect(() => {
    const h = () => {
      if (ref.current) {
        ref.current.style.height = `${(window.visualViewport || window).innerHeight || window.innerHeight}px`;
      }
    };
    h();
    window.addEventListener('resize', h);
    window.visualViewport?.addEventListener('resize', h);
    return () => {
      window.removeEventListener('resize', h);
      window.visualViewport?.removeEventListener('resize', h);
    };
  }, [ref]);
}

/* ═══════════════════════════════════════════
   RECRUITER — BROWSE
   ═══════════════════════════════════════════ */

function RecruiterBrowse({ onTalk, onBack }) {
  const [daemons, setDaemons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [idx,     setIdx]     = useState(0);

  useEffect(() => {
    fetch('/api/daemons')
      .then(r => r.json())
      .then(d => { setDaemons(d.daemons || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="screen landing">
      <div className="lc">
        <div className="gen-orb gen-orb-sm" />
        <p className="gen-txt">Searching for daemons...</p>
      </div>
    </div>
  );

  if (daemons.length === 0) return (
    <div className="screen landing">
      <div className="lc">
        <h2 className="lc-title2">No Daemons Yet</h2>
        <p className="lc-sub2">No candidates have deployed their daemons yet. Check back soon.</p>
        <button className="btn-link mt1" onClick={onBack}>← Back</button>
      </div>
    </div>
  );

  const d    = daemons[idx];
  const next = () => setIdx(i => (i + 1) % daemons.length);

  return (
    <div className="screen landing">
      <div className="lc">
        <div className="sh-status mb1">
          <span className="dot dot-green" />
          <span className="sh-st">{daemons.length} daemon{daemons.length !== 1 ? 's' : ''} available</span>
        </div>
        <Badge animal={d.tag || d.animal} size="lg" />
        <p className="daemon-animal">{d.tag || d.animal}</p>
        <p className="daemon-essence">{d.essence}</p>
        <div className="swipe-btns">
          <button className="btn-ghost"   onClick={next}>Skip →</button>
          <button className="btn-primary" onClick={() => onTalk(d.id)}>Interview Daemon</button>
        </div>
        <button className="btn-link mt1" onClick={onBack}>← Back</button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   TALK INTERFACE — orb conversation (recruiter / demo)
   ═══════════════════════════════════════════════════════════════ */

function TalkInterface({ sendMessage, initialPrompt, displayName, onBack }) {
  const [uiPhase,    setUiPhase]    = useState('init');
  const [textPhase,  setTextPhase]  = useState('hidden');
  const [displayText,setDisplayText]= useState('');
  const [material,      setMaterial]      = useState(null);
  const [materialBlend, setMaterialBlend] = useState(0);
  const [daemonAnimal,  setDaemonAnimal]  = useState(null);
  const [daemonTag,     setDaemonTag]     = useState(null);
  const [apiError,      setApiError]      = useState(null);
  const [started,       setStarted]       = useState(() => isAudioUnlocked());  // skip gate if audio already unlocked

  const history      = useRef([]);
  const timers       = useRef([]);
  const speakCancel  = useRef(false);
  const wrapRef      = useRef(null);
  const voiceIdRef   = useRef(null);

  const tts = useTTS();

  useViewportHeight(wrapRef);

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  const addTimer    = (fn, ms) => { const id = setTimeout(fn, ms); timers.current.push(id); return id; };

  // ── Surface text ──────────────────────────────────────────────────────────
  const surfaceText = useCallback((text) => {
    clearTimers();
    setDisplayText(text);
    setTextPhase('appearing');
    addTimer(() => setTextPhase('showing'), 1400);
    // Recruiter: slightly longer read time — they may be taking notes
    const words  = text.trim().split(/\s+/).length;
    const readMs = Math.min(24000, Math.max(6000, words * 300));
    addTimer(() => {
      setTextPhase('fading');
      addTimer(() => { setTextPhase('hidden'); setDisplayText(''); }, 950);
    }, 1400 + readMs);
  }, []); // eslint-disable-line

  // ── Speak and listen ──────────────────────────────────────────────────────
  const speakAndListen = useCallback(async (text) => {
    speakCancel.current = false;
    try {
      await tts.play(text, voiceIdRef.current);
    } catch {
      const words  = text.trim().split(/\s+/).length;
      await new Promise(r => {
        const id = setTimeout(r, Math.min(12000, Math.max(2500, words * 180)));
        timers.current.push(id);
      });
    }
    if (speakCancel.current) return;
    setUiPhase('listening');
  }, [tts]); // eslint-disable-line

  // ── Awaken: user's first tap unlocks audio and fires the greeting ─────────
  const awaken = useCallback(() => {
    if (started) return;
    unlockAudioContext();
    setStarted(true);
  }, [started]);

  // ── Init: daemon's opening (only fires after user taps to summon) ─────────
  useEffect(() => {
    if (!started) return;
    const msgs = [{ role: 'user', content: initialPrompt }];
    sendMessage(msgs).then((result) => {
      if (!result.ok) {
        setApiError(apiErrorMessage(result.error));
        setUiPhase('listening');
        return;
      }
      const { response, animal, material: m, tag, voiceId } = result;
      history.current = [...msgs, { role: 'assistant', content: response }];
      if (voiceId) voiceIdRef.current = voiceId;
      if (animal) setDaemonAnimal(animal);
      if (m)      { setMaterial(m); setMaterialBlend(1.0); }
      if (tag)    setDaemonTag(tag);
      setUiPhase('speaking');
      surfaceText(response);
      speakAndListen(response);
    });

    return () => { clearTimers(); tts.stop(); };
  }, [started]); // eslint-disable-line

  // ── Recruiter submits ─────────────────────────────────────────────────────
  // Same invariant as VoidInterface.handleSubmit — history.current is only mutated on success.
  const handleSubmit = useCallback(async (text) => {
    if (uiPhase === 'thinking' || uiPhase === 'init') return;

    speakCancel.current = true;
    tts.stop();
    clearTimers();
    setTextPhase('hidden');
    setDisplayText('');
    setApiError(null);
    setUiPhase('thinking');

    const proposedHistory = [...history.current, { role: 'user', content: text }];

    const result = await sendMessage(proposedHistory);
    if (!result.ok) {
      setApiError(apiErrorMessage(result.error));
      setUiPhase('listening');
      return;
    }
    const { response, animal, material: m, tag, voiceId } = result;
    history.current = [...proposedHistory, { role: 'assistant', content: response }];
    if (voiceId) voiceIdRef.current = voiceId;
    if (animal && !daemonAnimal) setDaemonAnimal(animal);
    if (m && !material)          { setMaterial(m); setMaterialBlend(1.0); }
    if (tag && !daemonTag)       setDaemonTag(tag);
    setUiPhase('speaking');
    surfaceText(response);
    speakAndListen(response);
  }, [uiPhase, daemonAnimal, material, daemonTag, tts, sendMessage, surfaceText, speakAndListen]);

  // Resolve display labels
  const tagStr   = daemonTag || (material && daemonAnimal ? `${material} ${daemonAnimal}` : null) || displayName || 'Daemon';
  const parts    = tagStr.split(' ');
  const matLabel = parts.length >= 2 ? parts[0]                : null;
  const aniLabel = parts.length >= 2 ? parts.slice(1).join(' ') : parts[0];

  return (
    <div className="talk-screen" ref={wrapRef}>

      <header className="talk-header">
        <button className="talk-back" onClick={onBack} aria-label="Go back">←</button>
        <div className="talk-identity">
          {matLabel && <span className="talk-material">{matLabel.toUpperCase()}</span>}
          <span className="talk-animal">{aniLabel.toUpperCase()}</span>
        </div>
        <div className="talk-online">
          <span className="dot dot-green" />
          <span className="talk-online-label">Online</span>
        </div>
      </header>

      <div className="void-screen talk-void" onClick={awaken}>
        <div className="void-ambient" aria-hidden="true" />
        <div aria-live="polite" className="sr-only">{displayText}</div>

        {/* Awaken hint — disappears once user taps */}
        {!started && (
          <div className="awaken-hint">Touch the orb to summon</div>
        )}

        <Orb
          text=""
          textPhase="hidden"
          uiPhase={uiPhase}
          exchangeCount={20}       /* Always sovereign — daemon is fully formed */
          material={material}
          materialBlend={materialBlend}
          onShatterComplete={() => {}}
        />

        {/* Voice input — hidden, auto-listen still active */}
        <div className="orb-input-anchor">
          <VoiceInput
            onSubmit={handleSubmit}
            autoListen={uiPhase === 'listening'}
            disabled={uiPhase === 'init' || uiPhase === 'thinking' || uiPhase === 'speaking'}
          />
        </div>

        {apiError && (
          <div
            role="alert"
            aria-live="assertive"
            style={{
              position: 'absolute', bottom: '90px', left: '50%', transform: 'translateX(-50%)',
              color: '#e8c97a', fontSize: '13px', opacity: 0.9, pointerEvents: 'none',
            }}
          >
            {apiError}
          </div>
        )}

      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   RECRUITER — TALK TO DAEMON
   ═══════════════════════════════════════════ */

function TalkChat({ daemonId, onBack }) {
  const sendMessage = useCallback(async (messages) => {
    let r;
    try {
      r = await fetch('/api/talk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: daemonId, messages }),
      });
    } catch {
      return { ok: false, error: 'network' };
    }
    if (!r.ok) {
      return { ok: false, error: r.status >= 500 ? 'http_5xx' : 'http_4xx' };
    }
    try {
      const data = await r.json();
      return { ok: true, ...data };
    } catch {
      return { ok: false, error: 'parse' };
    }
  }, [daemonId]);

  return (
    <TalkInterface
      sendMessage={sendMessage}
      initialPrompt="Hello. I'm a recruiter. Please introduce yourself."
      onBack={onBack}
    />
  );
}

/* ═══════════════════════════════════════════
   ZACH'S DAEMON — demo
   ═══════════════════════════════════════════ */

function ZachChat({ onBack }) {
  const sendMessage = useCallback(async (messages) => {
    let r;
    try {
      r = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages }),
      });
    } catch {
      return { ok: false, error: 'network' };
    }
    if (!r.ok) {
      return { ok: false, error: r.status >= 500 ? 'http_5xx' : 'http_4xx' };
    }
    try {
      const d = await r.json();
      return { ok: true, response: d.response, voiceId: d.voiceId };
    } catch {
      return { ok: false, error: 'parse' };
    }
  }, []);

  return (
    <TalkInterface
      sendMessage={sendMessage}
      initialPrompt="Hi, I just arrived. Please introduce yourself."
      displayName="Zach's Daemon"
      onBack={onBack}
    />
  );
}

/* ═══════════════════════════════════════════
   APP ROUTER
   ═══════════════════════════════════════════ */

export default function App() {
  const [view,            setView]            = useState('landing');
  const [deployedId,      setDeployedId]      = useState('');
  const [deployedProfile, setDeployedProfile] = useState(null);
  const [talkId,          setTalkId]          = useState('');

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const t = p.get('talk');
    if (t) { setTalkId(t); setView('talk'); }
  }, []);

  return (
    <div className="app">
      {view === 'landing' && (
        <LandingPage
          onSeeker={() => setView('void')}
          onRecruiter={() => setView('recruiter')}
          onZach={() => setView('zach')}
        />
      )}
      {view === 'void' && (
        <VoidInterface
          onDeploy={(id, profile) => {
            setDeployedId(id);
            setDeployedProfile(profile);
            setView('deployed');
          }}
        />
      )}
      {view === 'deployed' && (
        <DeploySuccess
          daemonId={deployedId}
          deployedProfile={deployedProfile}
          onBack={() => setView('landing')}
        />
      )}
      {view === 'recruiter' && (
        <RecruiterBrowse
          onTalk={(id) => { setTalkId(id); setView('talk'); }}
          onBack={() => setView('landing')}
        />
      )}
      {view === 'talk' && (
        <TalkChat daemonId={talkId} onBack={() => setView('landing')} />
      )}
      {view === 'zach' && (
        <ZachChat onBack={() => setView('landing')} />
      )}
    </div>
  );
}
